"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "../ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Textarea } from "../ui/textarea"
import { ArrowLeft, Home, Upload, X } from "lucide-react"

import { db, storage } from "../../firebaseconfig"; 
import { collection, addDoc } from "firebase/firestore";
import uploadService from "../../services/uploadService";
import { useAuth } from "../../context/AuthContext";
import { validarDNI, validarEmail, validarRequerido } from "../../services/validationService";
import emailjs from '@emailjs/browser';

export default function CreditoHipotecarioForm() {
  const { user } = useAuth() // Cambiado de currentUser a user para consistencia
  
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    dni: "",
    ingresos: "",
    valorPropiedad: "",
    montoSolicitado: "",
    plazo: "",
    tipoPropiedad: "",
    ubicacion: "",
    observaciones: "",
    estado: "pendiente",
    fechaCreacion: new Date().toISOString(),
  })

  const [archivos, setArchivos] = useState({
    reciboSueldo: [],
    fotoDNI: [],
    escrituras: []
  })

  const [uploading, setUploading] = useState(false)

  // Función para subir archivos usando el servicio mejorado
  const subirArchivos = async (archivos, tipoDocumento) => {
    if (!archivos || archivos.length === 0) {
      return []
    }

    try {
      console.log(`📋 Subiendo ${archivos.length} archivos de tipo: ${tipoDocumento}`)
      
      // Validar archivos antes de subirlos
      const erroresValidacion = uploadService.validarArchivos(
        archivos, 
        ['pdf', 'jpg', 'jpeg', 'png'], // tipos permitidos
        10 * 1024 * 1024 // 10MB máximo
      )

      if (erroresValidacion.length > 0) {
        throw new Error(`Errores de validación:\n${erroresValidacion.join('\n')}`)
      }

      // Usar el servicio de subida mejorado
      const ruta = `credito-hipotecario/${tipoDocumento}`
      const resultado = await uploadService.subirMultiplesArchivos(archivos, ruta)
      
      if (resultado.errores.length > 0) {
        console.warn('⚠️ Algunos archivos no se pudieron subir:', resultado.errores)
        if (resultado.exitosos.length > 0) {
          alert(`⚠️ Se subieron ${resultado.exitosos.length} de ${resultado.total} archivos.\nErrores: ${resultado.errores.map(e => e.error).join(', ')}`)
        }
      }

      if (resultado.exitosos.length === 0) {
        throw new Error('No se pudo subir ningún archivo')
      }

      console.log(`✅ Se subieron ${resultado.exitosos.length} archivos exitosamente`)
      return resultado.exitosos

    } catch (error) {
      console.error('❌ Error en subirArchivos:', error)
      throw error
    }
  }

  const enviarSolicitudAFirebase = async (data) => {
    try {
      setUploading(true);
      console.log("🚀 Iniciando envío de solicitud hipotecaria...");
      
      // Subir archivos usando el servicio mejorado
      const urls = {};
      
      // Subir recibos de sueldo
      if (archivos.reciboSueldo.length > 0) {
        console.log(`📄 Subiendo ${archivos.reciboSueldo.length} recibos de sueldo...`);
        const resultadoRecibos = await subirArchivos(archivos.reciboSueldo, 'recibos-sueldo');
        urls.reciboSueldoURLs = resultadoRecibos.map(r => r.url);
        console.log(`✅ ${resultadoRecibos.length} recibos subidos exitosamente`);
      }
      
      // Subir fotos de DNI
      if (archivos.fotoDNI.length > 0) {
        console.log(`🆔 Subiendo ${archivos.fotoDNI.length} fotos de DNI...`);
        const resultadoDNI = await subirArchivos(archivos.fotoDNI, 'fotos-dni');
        urls.fotoDNIURLs = resultadoDNI.map(r => r.url);
        console.log(`✅ ${resultadoDNI.length} fotos de DNI subidas exitosamente`);
      }

      // Subir escrituras (específico para hipotecario)
      if (archivos.escrituras.length > 0) {
        console.log(`🏠 Subiendo ${archivos.escrituras.length} escrituras...`);
        const resultadoEscrituras = await subirArchivos(archivos.escrituras, 'escrituras');
        urls.escriturasURLs = resultadoEscrituras.map(r => r.url);
        console.log(`✅ ${resultadoEscrituras.length} escrituras subidas exitosamente`);
      }

      console.log("📝 Todos los archivos subidos, guardando en Firestore...");

      // Obtener datos del usuario autenticado si existe (para tracking de agencia)
      const userData = user ? {
        id: user.uid,
        email: user.email,
        nombre: user.displayName || user.email?.split('@')[0] || 'Usuario'
      } : null;

      // Usar el mismo formato que los otros formularios (personal y prendario)
      const archivosData = {
        // Arrays directos de nombres de archivos
        reciboSueldo: archivos.reciboSueldo.map(file => file.name),
        fotoDNI: archivos.fotoDNI.map(file => file.name),
        escrituras: archivos.escrituras.map(file => file.name),
        // Agregar las URLs de descarga
        ...urls
      };

      // Guardar en Firestore en la colección correcta: creditosHipotecarios
      const solicitudData = {
        ...data,
        archivos: archivosData,
        archivosSubidos: true,
        totalArchivos: archivos.reciboSueldo.length + archivos.fotoDNI.length + archivos.escrituras.length,
        tipoCredito: 'hipotecario',
        fechaCreacion: new Date().toISOString(),
        estado: 'pendiente',
        // Información del usuario que carga la solicitud (opcional para tracking de agencias)
        agenciaUsuario: userData || null,
        cargadoPor: userData?.email || 'solicitud-publica',
        origen: userData ? 'agencia' : 'web-publica'
      };

      // Usar la colección específica para hipotecarios
      const docRef = await addDoc(collection(db, 'creditosHipotecarios'), solicitudData);
      console.log("✅ Solicitud hipotecaria guardada exitosamente con ID:", docRef.id);
      console.log("📊 Archivos guardados con nuevo formato:", archivosData);
      console.log("🔗 URLs incluidas:", Object.keys(urls));

      // Mostrar mensaje de éxito personalizado según el origen
      const mensajeExit = userData 
        ? `✅ Solicitud de crédito hipotecario enviada correctamente!\n\nID de solicitud: ${docRef.id.substring(0, 8)}...\n\nPodrás ver el estado de la solicitud en el panel administrativo.`
        : `✅ Solicitud de crédito hipotecario enviada correctamente!\n\nID de solicitud: ${docRef.id.substring(0, 8)}...\n\nTe contactaremos pronto al número de teléfono proporcionado para coordinar el seguimiento de tu solicitud.`;
      
      alert(mensajeExit);

      // Enviar email de notificación (comentado por ahora)
     /* const templateParams = {
        to_email: 'gestiongamaan@gmail.com',
        from_name: `${data.nombre} ${data.apellido}`,
        message: `Nueva solicitud de crédito hipotecario.\n\nDatos del solicitante:\n- Nombre: ${data.nombre} ${data.apellido}\n- Email: ${data.email}\n- Teléfono: ${data.telefono}\n- Monto solicitado: $${data.montoSolicitado}\n- Plazo: ${data.plazo} meses\n- Propiedad a hipotecar: ${data.propiedadDomicilio}${data.agenciaUsuario ? `\n- Agencia: ${data.agenciaUsuario}` : ''}\n\nID de solicitud: ${docRef.id}`
      };

      await emailjs.send(
        'service_99xp42l',
        'template_tnwzmld',
        templateParams,
        'uWt6_hqQPHWGEFUlW'
      );

      alert('✅ Solicitud enviada correctamente');
      console.log("✅ Email de notificación enviado exitosamente");*/
      
      // Limpiar el formulario
      setFormData({
        nombre: "",
        email: "",
        telefono: "",
        dni: "",
        ingresos: "",
        valorPropiedad: "",
        montoSolicitado: "",
        plazo: "",
        tipoPropiedad: "",
        ubicacion: "",
        observaciones: "",
        estado: "pendiente",
        fechaCreacion: new Date().toISOString(),
      });
      setArchivos({
        reciboSueldo: [],
        fotoDNI: [],
        escrituras: []
      });

    } catch (error) {
      console.error('❌ Error completo al enviar solicitud:', error);
      let mensajeError = 'Error al enviar la solicitud. ';
      
      if (error.message) {
        mensajeError += error.message;
      } else if (error.code) {
        mensajeError += `Código de error: ${error.code}`;
      } else {
        mensajeError += 'Por favor, inténtelo de nuevo.';
      }
      
      alert(mensajeError);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // ✅ Validaciones mejoradas
    const validacionDNI = validarDNI(formData.dni);
    if (!validacionDNI.valido) {
      alert("❌ " + validacionDNI.error);
      return;
    }

    const validacionEmail = validarEmail(formData.email);
    if (!validacionEmail.valido) {
      alert("❌ " + validacionEmail.error);
      return;
    }

    // Validar campos requeridos
    const camposRequeridos = [
      { valor: formData.nombre, nombre: "Nombre Completo" },
      { valor: formData.telefono, nombre: "Teléfono" },
      { valor: formData.ingresos, nombre: "Ingresos Mensuales" },
      { valor: formData.valorPropiedad, nombre: "Valor de la Propiedad" },
      { valor: formData.montoSolicitado, nombre: "Monto Solicitado" },
      { valor: formData.plazo, nombre: "Plazo" },
      { valor: formData.tipoPropiedad, nombre: "Tipo de Propiedad" },
      { valor: formData.ubicacion, nombre: "Ubicación" },
    ];

    for (const campo of camposRequeridos) {
      const validacion = validarRequerido(campo.valor, campo.nombre);
      if (!validacion.valido) {
        alert("❌ " + validacion.error);
        return;
      }
    }
    
    // Validar que al menos la foto de DNI esté presente
    if (archivos.fotoDNI.length === 0) {
      alert("❌ Por favor sube la Foto de DNI (documento obligatorio)");
      return;
    }
    
    // Validar recibo de sueldo (opcional pero informar al usuario)
    if (archivos.reciboSueldo.length === 0) {
      const confirmar = window.confirm("No has subido recibo de sueldo. ¿Deseas continuar sin este documento? Podrás subirlo más tarde si es necesario.");
      if (!confirmar) {
        return;
      }
    }
    
    // Confirmar envío si no se subieron escrituras
    if (archivos.escrituras.length === 0) {
      const confirmar = window.confirm("No has subido escrituras de la propiedad. ¿Deseas continuar sin estos documentos? Podrás subirlos más tarde.");
      if (!confirmar) {
        return;
      }
    }
    
    await enviarSolicitudAFirebase(formData)
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleFileChange = (tipo, files) => {
    if (files && files.length > 0) {
      const validFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Validar tipo de archivo
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
        if (!allowedTypes.includes(file.type)) {
          alert(`El archivo ${file.name} no es válido. Solo se permiten archivos PDF, JPG, JPEG o PNG`)
          continue;
        }
        
        // Validar tamaño (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert(`El archivo ${file.name} supera los 5MB permitidos`)
          continue;
        }
        
        validFiles.push(file);
      }
      
      if (validFiles.length > 0) {
        setArchivos(prev => ({ 
          ...prev, 
          [tipo]: [...prev[tipo], ...validFiles]
        }));
      }
    }
  }

  const removeFile = (tipo, index) => {
    setArchivos(prev => ({
      ...prev,
      [tipo]: prev[tipo].filter((_, i) => i !== index)
    }));
  }

  const FileUpload = ({ label, tipo, required = false, maxFiles = 5, description }) => (
    <div className="space-y-2">
      <Label htmlFor={tipo}>
        {label} {required && '*'}
        <span className="text-sm text-gray-500 ml-2">
          ({archivos[tipo].length}/{maxFiles} archivos)
        </span>
      </Label>
      
      {/* Mostrar archivos seleccionados */}
      {archivos[tipo].length > 0 && (
        <div className="space-y-2 mb-4">
          {archivos[tipo].map((file, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-green-600" />
                <span className="text-sm text-gray-700">{file.name}</span>
                <span className="text-xs text-gray-500">
                  ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(tipo, index)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Zona de subida */}
      {archivos[tipo].length < maxFiles && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-1">
            {description || "Arrastra y suelta tus archivos aquí, o haz clic para seleccionar"}
          </p>
          <p className="text-xs text-gray-400 mb-2">
            PDF, JPG, JPEG o PNG (máx. 5MB cada uno)
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Puedes subir hasta {maxFiles} archivos
          </p>
          <input
            id={tipo}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            onChange={(e) => handleFileChange(tipo, e.target.files)}
            className="hidden"
          />
          <label
            htmlFor={tipo}
            className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer"
          >
            Seleccionar archivos
          </label>
        </div>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Link>
          </Button>
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Home className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-3xl">Solicitud de Crédito Hipotecario</CardTitle>
            <CardDescription>Completa el formulario para solicitar tu crédito hipotecario</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre Completo *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleChange("nombre", e.target.value)}
                    placeholder="Ingresa tu nombre completo"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dni">DNI *</Label>
                  <Input
                    id="dni"
                    value={formData.dni}
                    onChange={(e) => handleChange("dni", e.target.value)}
                    placeholder="12345678"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="tu@email.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono *</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => handleChange("telefono", e.target.value)}
                    placeholder="+54 11 1234-5678"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ingresos">Ingresos Mensuales *</Label>
                  <Input
                    id="ingresos"
                    value={formData.ingresos}
                    onChange={(e) => handleChange("ingresos", e.target.value)}
                    placeholder="$500,000"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valorPropiedad">Valor de la Propiedad *</Label>
                  <Input
                    id="valorPropiedad"
                    value={formData.valorPropiedad}
                    onChange={(e) => handleChange("valorPropiedad", e.target.value)}
                    placeholder="$50,000,000"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="montoSolicitado">Monto Solicitado *</Label>
                  <Input
                    id="montoSolicitado"
                    value={formData.montoSolicitado}
                    onChange={(e) => handleChange("montoSolicitado", e.target.value)}
                    placeholder="$40,000,000"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plazo">Plazo (años) *</Label>
                  <Select onValueChange={(value) => handleChange("plazo", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el plazo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 años</SelectItem>
                      <SelectItem value="15">15 años</SelectItem>
                      <SelectItem value="20">20 años</SelectItem>
                      <SelectItem value="25">25 años</SelectItem>
                      <SelectItem value="30">30 años</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipoPropiedad">Tipo de Propiedad *</Label>
                  <Select onValueChange={(value) => handleChange("tipoPropiedad", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casa">Casa</SelectItem>
                      <SelectItem value="departamento">Departamento</SelectItem>
                      <SelectItem value="ph">PH</SelectItem>
                      <SelectItem value="duplex">Duplex</SelectItem>
                      <SelectItem value="terreno">Terreno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ubicacion">Ubicación *</Label>
                  <Input
                    id="ubicacion"
                    value={formData.ubicacion}
                    onChange={(e) => handleChange("ubicacion", e.target.value)}
                    placeholder="Ciudad, Provincia"
                    required
                  />
                </div>
              </div>

              {/* Sección de documentos */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-green-700">Documentación Requerida</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <FileUpload 
                    label="Recibos de Sueldo" 
                    tipo="reciboSueldo" 
                    required={true}
                    maxFiles={3}
                    description="Sube hasta 3 recibos de sueldo (últimos meses)"
                  />
                  <FileUpload 
                    label="Fotos DNI" 
                    tipo="fotoDNI" 
                    required={true}
                    maxFiles={2}
                    description="Sube fotos del anverso y reverso de tu DNI"
                  />
                </div>
                
                {/* Documentos específicos para hipotecario */}
                <div className="mt-6">
                  <FileUpload 
                    label="Escrituras de la Propiedad" 
                    tipo="escrituras" 
                    required={false}
                    maxFiles={5}
                    description="Escrituras, títulos o documentos de la propiedad a hipotecar (opcional)"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observaciones">Observaciones</Label>
                <Textarea
                  id="observaciones"
                  value={formData.observaciones}
                  onChange={(e) => handleChange("observaciones", e.target.value)}
                  placeholder="Información adicional que consideres relevante"
                  className="min-h-[100px]"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={uploading}
              >
                {uploading ? "Enviando solicitud..." : "Enviar Solicitud de Crédito Hipotecario"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}